import { z } from 'zod';

import { Method, zParsedSelectorString } from './parsedSelectorString';

export enum ResourceTypeId {
  Model = 1,
  Source = 2,
  Test = 3,
  Snapshot = 4,
  Exposure = 5,
  Macro = 6,
  Seed = 7,
  Metric = 8,
  SemanticModel = 9,
  SavedQuery = 10,
}

/**
 * This order must match the order in codex.
 */
export const codexOrderedResourceTypes = [
  'model',
  'source',
  'test',
  'snapshot',
  'exposure',
  'macro',
  'seed',
  'metric',
  'semantic_model',
  'saved_query',
] as const;

export const ResourceNodeTypeMap = codexOrderedResourceTypes.reduce(
  (prev, key, index) => ({ ...prev, [key]: index + 1 }),
  {} as any,
) as Record<ResourceType, number>;

export const ResourceNodeIdMap: Record<number, ResourceType> = Object.entries(
  ResourceNodeTypeMap,
).reduce((prev, [key, num]) => ({ ...prev, [num]: key }), {});

/**
 * Resource types that are supported by Discovery API
 */
export const zResourceType = z.enum(codexOrderedResourceTypes);

export type ResourceType = z.infer<typeof zResourceType>;

const unsupportedMethods = new Set<Method>([
  'source_status',
  'result',
  'source_status',
  'state',
  'test_name',
  'test_type',
]);

export const supportedConfigArgs = [
  'config.materialized:',
  'config.database:',
  'config.schema:',
] as const;

const supportedMethodArgsSet: Readonly<Set<string>> = new Set([...supportedConfigArgs]);

export const testTypes = [
  'data',
  'generic',
  'singular',
  'unit',
  /** Deprecated, but supported */
  'schema',
] as const;

/**
 * `schema` is deprecated, but still supported
 */
export type TestType = (typeof testTypes)[number];

/**
 * While {@link zParsedSelectorString} represents a 1:1 behavior of how dbt-core
 * handles selector syntax, this parser represents the subset of selectors that
 * are supported by Discovery API, as well as any business logic that we
 * want to apply on top of the core logic.
 */
export const zSupportedSelector = z.string().transform((str, ctx) => {
  const parsedSelector = zParsedSelectorString.safeParse(str);
  if (!parsedSelector.success) {
    parsedSelector.error.issues.forEach((issue) => {
      ctx.addIssue(issue);
    });
    return z.NEVER;
  }

  let index = 0;
  return parsedSelector.data.map((intersections) => {
    return intersections.map((selector) => {
      const startIndex = index;
      const endIndex = index + selector.raw.length;
      const path = [startIndex, endIndex];
      index = endIndex + 1;
      if (unsupportedMethods.has(selector.method)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${selector.method} is not yet supported`,
          path,
        });
        return z.NEVER;
      }

      const methodArgs = selector.method_arguments.join('.');
      if (methodArgs) {
        const withMethodArgs = `${selector.method}.${methodArgs}:`;
        if (!supportedMethodArgsSet.has(withMethodArgs)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `'${withMethodArgs}' is not supported`,
            path,
          });
          return z.NEVER;
        }
      }

      return selector;
    });
  });
});
